/**
 * RegisterPage Component
 * 
 * Provides user registration interface with invitation token validation.
 * 
 * Features:
 * - Invitation token from URL query parameter
 * - Email and password input validation
 * - Password confirmation matching
 * - Password policy validation (moderate: 8+ chars, 1 number/special)
 * - Error handling and user feedback
 * - Auto-login after successful registration
 * - Redirect to upload page on success
 */

import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/auth'
import { UserPlus, Mail, Lock, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { safeReturnUrl } from '../utils/safeReturnUrl'
import { normalizeErrorMessage } from '../utils/dataValidator'

const RegisterPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [inviteToken, setInviteToken] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ 
    email?: string
    password?: string
    confirmPassword?: string
    inviteToken?: string
  }>({})
  
  const { register, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Get invitation token from URL on mount
  const tokenFromUrl = searchParams.get('token') || ''
  
  // Initialize invite token from URL if available
  useEffect(() => {
    if (tokenFromUrl && !inviteToken) {
      setInviteToken(tokenFromUrl)
    }
  // inviteToken is set by this effect, so depending on it would re-run the
  // effect with the value it just wrote. The guard above already makes it run
  // once. The directive must be the line immediately before the dependency
  // array: the rule reports there, and a continuation comment in between
  // absorbs the disable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl])
  
  // Get return URL from query params or default to /projects
  const returnUrl = safeReturnUrl(searchParams.get('returnUrl'))
  
  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnUrl, { replace: true })
    }
  }, [isAuthenticated, navigate, returnUrl])
  
  /**
   * Validate email format
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }
  
  /**
   * Validate password policy (moderate: 8+ chars, 1 number OR special char)
   */
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    
    const hasNumber = /\d/.test(password)
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    
    if (!hasNumber && !hasSpecial) {
      return 'Password must contain at least one number or special character'
    }
    
    // Check if password contains email (if email is entered)
    if (email && password.toLowerCase().includes(email.toLowerCase())) {
      return 'Password cannot contain your email address'
    }
    
    // Check if password contains email local part (before @)
    if (email) {
      const emailLocal = email.split('@')[0]
      if (emailLocal.length >= 3 && password.toLowerCase().includes(emailLocal.toLowerCase())) {
        return 'Password cannot contain part of your email address'
      }
    }
    
    return null
  }
  
  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}
    
    if (!inviteToken.trim()) {
      newErrors.inviteToken = 'Invitation token is required'
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    const passwordError = validatePassword(password)
    if (!password) {
      newErrors.password = 'Password is required'
    } else if (passwordError) {
      newErrors.password = passwordError
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    // Clear previous errors
    setErrors({})
    
    // Validate form
    if (!validateForm()) {
      return
    }
    
    setIsLoading(true)
    
    try {
      // Lowercase email for consistency with backend
      await register(email.toLowerCase().trim(), password, inviteToken)
      
      // Success - show toast and redirect
      toast.success('Registration successful! Welcome!')
      navigate(returnUrl, { replace: true })
    } catch (error) {
      // Handle registration errors
      const errorMessage = normalizeErrorMessage(error, 'Registration failed. Please try again.')
      
      // Check for specific error types
      if (errorMessage.includes('Invalid invitation token')) {
        setErrors({ inviteToken: 'Invalid or expired invitation token' })
        toast.error('Invalid or expired invitation token')
      } else if (errorMessage.includes('already exists')) {
        setErrors({ email: 'An account with this email already exists' })
        toast.error('An account with this email already exists')
      } else if (errorMessage.includes('email does not match')) {
        setErrors({ email: 'Email does not match the invitation' })
        toast.error('Email does not match the invitation')
      } else if (errorMessage.includes('Password')) {
        setErrors({ password: errorMessage })
        toast.error(errorMessage)
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-soft">
      <div className="max-w-md w-full mx-4">
        {/* Registration Card */}
        <div className="auth-card">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="auth-icon-shell">
              <UserPlus className="w-8 h-8 text-ink" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-ink">
              Create Account
            </h1>
            <p className="text-sm text-ink-soft">
              Complete your registration to get started
            </p>
          </div>
          
          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2 text-ink">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-ink-soft" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (errors.email) setErrors({ ...errors, email: undefined })
                  }}
                  className={`auth-input ${
                    errors.email
                      ? 'border-red-500 focus:ring-red-500'
                      : 'auth-input-default'
                  }`}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email}
                </p>
              )}
            </div>
            
            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2 text-ink">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-ink-soft" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (errors.password) setErrors({ ...errors, password: undefined })
                  }}
                  className={`auth-input ${
                    errors.password
                      ? 'border-red-500 focus:ring-red-500'
                      : 'auth-input-default'
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.password}
                </p>
              )}
              <p className="mt-1 text-xs text-ink-soft">
                Minimum 8 characters, at least 1 number or special character
              </p>
            </div>
            
            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2 text-ink">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-ink-soft" />
                </div>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined })
                  }}
                  className={`auth-input ${
                    errors.confirmPassword
                      ? 'border-red-500 focus:ring-red-500'
                      : 'auth-input-default'
                  }`}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.confirmPassword}
                </p>
              )}
              {password && confirmPassword && password === confirmPassword && !errors.confirmPassword && (
                <p className="mt-1 text-sm text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Passwords match
                </p>
              )}
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="auth-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  Create Account
                </>
              )}
            </button>
          </form>
          
          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-ink-soft">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium hover:underline text-ink"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
        
        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-ink-soft">
            Need an invitation? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
